import { getOrgIntegrations } from '@/api/integration'
import { getOrgForSidebar } from '@/api/org'
import { getKkmConfig } from '@/api/kkm-receipt'
import { preloadImage } from '@/utils/preloadImage'
import logger from '@/utils/logger'

function normalizeOrgId(orgId) {
  return orgId === undefined || orgId === null ? null : String(orgId)
}

function shouldShowCabinetLogo(org) {
  return Boolean(org?.logo) && org?.logoApplyCabinet !== false
}

function preloadCabinetLogo(org) {
  return preloadImage(shouldShowCabinetLogo(org) ? org.logo : '')
}

function clearOrgIntegrations(state) {
  state.integrations = []
  state.integrationsOrgId = null
}

function clearKkmConfig(state) {
  state.kkmConfig = null
  state.kkmConfigOrgId = null
}

function patchCurrentOrg(state, payload, assign) {
  const orgId = normalizeOrgId(payload?.orgId)
  if (!orgId) return

  const currentId = normalizeOrgId(state.currentOrg?.id)
  if (currentId && currentId !== orgId) return

  const nextOrg = state.currentOrg
    ? { ...state.currentOrg }
    : { id: payload.orgId }

  assign(nextOrg, payload)
  state.currentOrg = nextOrg
}

function assignColorFields(org, payload, colorKey, applyKey) {
  if (payload[colorKey] !== undefined) {
    org[colorKey] = payload[colorKey] || null
  }

  if (payload[applyKey] !== undefined) {
    org[applyKey] = payload[applyKey] === true
  }
}

/** In-flight запросы config ККТ — чтобы Sidebar и календарь не били API дважды. */
const kkmConfigInflightByOrg = Object.create(null)

const KEEP_ORG_FIELDS = [
  'directorId',
  'logo',
  'logoApplyCabinet',
  'accentColor',
  'accentApplyCabinet',
  'mainColor',
  'mainApplyCabinet',
]

/**
 * Этот модуль управляет состоянием данных организации в приложении.
 * Обеспечивает централизованное хранение и обновление информации об организации,
 * что позволяет компонентам (например, сайдбару) автоматически обновляться
 * при изменении данных организации.
 *
 * Например, логотип в настройках ЛК обновляет сайдбар через этот модуль
 * без перезагрузки страницы.
 */

export const state = () => ({
  // Текущие данные организации
  currentOrg: null,
  // Интеграции, загруженные для текущей организации
  integrations: [],
  integrationsOrgId: null,
  integrationsLoading: false,
  // Конфиг ККТ (integrations/.../config) — общий для Sidebar и календаря
  kkmConfig: null,
  kkmConfigOrgId: null,
  // Состояние загрузки данных
  loading: false,
  // Информация об ошибках
  error: null,
})

export const mutations = {
  /**
   * Устанавливает данные текущей организации
   * @param {Object} state - Состояние store
   * @param {Object} org - Объект с данными организации
   */
  SET_CURRENT_ORG(state, org) {
    const previousOrgId = normalizeOrgId(state.currentOrg?.id)
    const nextOrgId = normalizeOrgId(org?.id)
    let nextOrg = org || null

    if (
      state.currentOrg &&
      nextOrg &&
      previousOrgId &&
      previousOrgId === nextOrgId
    ) {
      nextOrg = { ...state.currentOrg, ...nextOrg }
      KEEP_ORG_FIELDS.forEach((key) => {
        if (org[key] === undefined) nextOrg[key] = state.currentOrg[key]
      })
    }

    state.currentOrg = nextOrg
    state.error = null

    if (
      nextOrgId &&
      ((previousOrgId && previousOrgId !== nextOrgId) ||
        (state.integrationsOrgId && state.integrationsOrgId !== nextOrgId))
    ) {
      clearOrgIntegrations(state)
    }

    if (
      nextOrgId &&
      ((previousOrgId && previousOrgId !== nextOrgId) ||
        (state.kkmConfigOrgId && state.kkmConfigOrgId !== nextOrgId))
    ) {
      clearKkmConfig(state)
    }
  },

  SET_ORG_INTEGRATIONS(state, { orgId, integrations }) {
    state.integrations = integrations
    state.integrationsOrgId = normalizeOrgId(orgId)
    state.error = null
  },

  UPSERT_ORG_INTEGRATION(state, integration) {
    const hasId = integration.id !== undefined && integration.id !== null
    const hasSlug = Boolean(integration.slug)
    const index = state.integrations.findIndex(
      (item) =>
        (hasId && String(item.id) === String(integration.id)) ||
        (hasSlug && item.slug === integration.slug)
    )

    if (index === -1) {
      state.integrations.push(integration)
      return
    }

    state.integrations.splice(index, 1, {
      ...state.integrations[index],
      ...integration,
    })
  },

  CLEAR_ORG_INTEGRATIONS(state) {
    clearOrgIntegrations(state)
  },

  SET_INTEGRATIONS_LOADING(state, loading) {
    state.integrationsLoading = loading
  },

  SET_KKM_CONFIG(state, { orgId, config }) {
    state.kkmConfig = config
    state.kkmConfigOrgId = normalizeOrgId(orgId)
  },

  CLEAR_KKM_CONFIG(state) {
    clearKkmConfig(state)
  },

  /**
   * Обновляет только имя организации
   * @param {Object} state - Состояние store
   * @param {string} name - Новое имя организации
   */
  UPDATE_ORG_NAME(state, name) {
    if (state.currentOrg) {
      state.currentOrg.name = name
    }
  },

  /**
   * Устанавливает имя организации (альтернативный метод)
   * @param {Object} state - Состояние store
   * @param {string} name - Имя организации
   */
  SET_ORG_NAME(state, name) {
    if (state.currentOrg) {
      state.currentOrg.name = name
    }
  },

  /**
   * Обновляет логотип и флаг показа в кабинете у текущей организации.
   */
  UPDATE_ORG_LOGO(state, payload) {
    const orgId = normalizeOrgId(payload?.orgId)
    if (!orgId) return

    const currentId = normalizeOrgId(state.currentOrg?.id)
    if (currentId && currentId !== orgId) return

    const nextOrg = state.currentOrg
      ? { ...state.currentOrg }
      : { id: payload.orgId }

    if (payload.logo !== undefined) {
      nextOrg.logo = payload.logo || null
    }

    if (payload.logoApplyCabinet !== undefined) {
      nextOrg.logoApplyCabinet = payload.logoApplyCabinet === true
    }

    state.currentOrg = nextOrg
  },

  UPDATE_ORG_ACCENT(state, payload) {
    patchCurrentOrg(state, payload, (org, next) => {
      assignColorFields(org, next, 'accentColor', 'accentApplyCabinet')
    })
  },

  UPDATE_ORG_MAIN(state, payload) {
    patchCurrentOrg(state, payload, (org, next) => {
      assignColorFields(org, next, 'mainColor', 'mainApplyCabinet')
    })
  },

  /**
   * Устанавливает состояние загрузки
   * @param {Object} state - Состояние store
   * @param {boolean} loading - Состояние загрузки
   */
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  /**
   * Устанавливает информацию об ошибке
   * @param {Object} state - Состояние store
   * @param {string|null} error - Сообщение об ошибке
   */
  SET_ERROR(state, error) {
    state.error = error
  },
}

export const actions = {
  /**
   * Устанавливает данные организации в store
   * Используется при загрузке данных организации в компонентах
   *
   * @param {Object} context - Контекст Vuex (commit, state, etc.)
   * @param {Object} org - Объект с данными организации
   *
   * @example
   * // В компоненте при загрузке данных организации
   * const orgResponse = await getOrgById(orgId)
   * this.$store.dispatch('org/setCurrentOrg', orgResponse.data)
   */
  setCurrentOrg({ commit }, org) {
    commit('SET_CURRENT_ORG', org)
  },

  /**
   * Организация для сайдбара (имя, логотип, флаг кабинета).
   * Дедуплицирует запрос, если данные уже в store.
   */
  async fetchForSidebar({ commit, state }, orgId) {
    const id = normalizeOrgId(orgId)
    if (!id) {
      throw new Error('orgId обязателен для загрузки данных сайдбара')
    }

    const current = state.currentOrg
    if (
      current &&
      normalizeOrgId(current.id) === id &&
      current.logoApplyCabinet !== undefined &&
      current.accentColor !== undefined &&
      current.mainColor !== undefined
    ) {
      await preloadCabinetLogo(current)
      return current
    }

    const { data } = await getOrgForSidebar(id)
    commit('SET_CURRENT_ORG', data)
    await preloadCabinetLogo(data)
    return data
  },

  async fetchOrgIntegrations({ commit, state }, payload) {
    const options =
      payload !== null && typeof payload === 'object'
        ? payload
        : { orgId: payload }
    const orgId = normalizeOrgId(options.orgId)

    if (!orgId) {
      throw new Error('orgId обязателен для загрузки интеграций организации')
    }

    if (!options.force && state.integrationsOrgId === orgId) {
      return state.integrations
    }

    if (state.integrationsOrgId && state.integrationsOrgId !== orgId) {
      commit('CLEAR_ORG_INTEGRATIONS')
    }

    commit('SET_INTEGRATIONS_LOADING', true)

    try {
      const { data } = await getOrgIntegrations(orgId)

      if (!Array.isArray(data)) {
        throw new TypeError(
          'API вернул некорректный список интеграций организации'
        )
      }

      commit('SET_ORG_INTEGRATIONS', { orgId, integrations: data })
      return data
    } catch (error) {
      logger.error('Ошибка загрузки интеграций организации:', error)
      commit(
        'SET_ERROR',
        error?.message || 'Ошибка загрузки интеграций организации'
      )
      throw error
    } finally {
      commit('SET_INTEGRATIONS_LOADING', false)
    }
  },

  updateOrgIntegration({ commit }, integration) {
    if (!integration?.id && !integration?.slug) {
      throw new Error('Нельзя обновить интеграцию без id или slug')
    }

    commit('UPSERT_ORG_INTEGRATION', integration)
  },

  clearOrgIntegrations({ commit }) {
    commit('CLEAR_ORG_INTEGRATIONS')
  },

  /**
   * Конфиг ККТ для org. Кэш + in-flight dedupe (календарь и Sidebar на одном заходе).
   * @param {{ orgId: number|string, force?: boolean }} payload
   */
  async fetchKkmConfig({ commit, state }, payload) {
    const options =
      payload !== null && typeof payload === 'object'
        ? payload
        : { orgId: payload }
    const orgId = normalizeOrgId(options.orgId)

    if (!orgId) {
      throw new Error('orgId обязателен для загрузки конфига ККТ')
    }

    if (
      !options.force &&
      state.kkmConfigOrgId === orgId &&
      state.kkmConfig != null
    ) {
      return state.kkmConfig
    }

    if (!options.force && kkmConfigInflightByOrg[orgId]) {
      return kkmConfigInflightByOrg[orgId]
    }

    if (state.kkmConfigOrgId && state.kkmConfigOrgId !== orgId) {
      commit('CLEAR_KKM_CONFIG')
    }

    const requestPromise = (async () => {
      const { data } = await getKkmConfig(orgId)
      commit('SET_KKM_CONFIG', { orgId, config: data })
      return data
    })()

    kkmConfigInflightByOrg[orgId] = requestPromise

    try {
      return await requestPromise
    } catch (error) {
      logger.error('Ошибка загрузки конфига ККТ:', error)
      throw error
    } finally {
      delete kkmConfigInflightByOrg[orgId]
    }
  },

  clearKkmConfig({ commit }) {
    commit('CLEAR_KKM_CONFIG')
  },

  /**
   * Обновляет имя организации
   * Используется после успешного сохранения изменений в профиле организации
   *
   * @param {Object} context - Контекст Vuex (commit, state, etc.)
   * @param {string} name - Новое имя организации
   *
   * @example
   * // В компоненте профиля после успешного сохранения
   * await updateOrg(orgId, { name: newName })
   * this.$store.dispatch('org/updateOrgName', newName)
   */
  updateOrgName({ commit }, name) {
    commit('UPDATE_ORG_NAME', name)
  },

  /**
   * Устанавливает имя организации (альтернативный метод)
   * @param {Object} context - Контекст Vuex (commit, state, etc.)
   * @param {string} name - Имя организации
   */
  setOrgName({ commit }, name) {
    commit('SET_ORG_NAME', name)
  },

  /**
   * Обновляет логотип / флаг кабинета в сайдбаре без перезагрузки.
   * @param {{ orgId: number|string, logo?: string|null, logoApplyCabinet?: boolean }} payload
   */
  updateOrgLogo({ commit }, payload) {
    commit('UPDATE_ORG_LOGO', payload)
  },

  updateOrgAccent({ commit }, payload) {
    commit('UPDATE_ORG_ACCENT', payload)
  },

  updateOrgMain({ commit }, payload) {
    commit('UPDATE_ORG_MAIN', payload)
  },

  /**
   * Устанавливает состояние загрузки
   * @param {Object} context - Контекст Vuex (commit, state, etc.)
   * @param {boolean} loading - Состояние загрузки
   */
  setLoading({ commit }, loading) {
    commit('SET_LOADING', loading)
  },

  /**
   * Устанавливает информацию об ошибке
   * @param {Object} context - Контекст Vuex (commit, state, etc.)
   * @param {string|null} error - Сообщение об ошибке
   */
  setError({ commit }, error) {
    commit('SET_ERROR', error)
  },
}

export const getters = {
  /**
   * Получает данные текущей организации
   * @param {Object} state - Состояние store
   * @returns {Object|null} Данные организации или null
   *
   * @example
   * // В computed свойстве компонента
   * computed: {
   *   orgFromStore() {
   *     return this.$store.getters['org/getCurrentOrg']
   *   }
   * }
   */
  getCurrentOrg: (state) => state.currentOrg,

  getOrgIntegrations: (state) => state.integrations,

  isOrgIntegrationsLoading: (state) => state.integrationsLoading,

  isOrgIntegrationsLoaded: (state) => (orgId) =>
    state.integrationsOrgId === normalizeOrgId(orgId),

  getIntegrationBySlug: (state) => (slug) =>
    state.integrations.find((integration) => integration.slug === slug) || null,

  isIntegrationEnabled: (state) => (slug) =>
    state.integrations.some(
      (integration) => integration.slug === slug && integration.enabled === true
    ),

  getKkmConfig: (state) => state.kkmConfig,

  isKkmConfigLoaded: (state) => (orgId) =>
    state.kkmConfigOrgId === normalizeOrgId(orgId) && state.kkmConfig != null,

  /**
   * Проверяет, идет ли загрузка данных
   * @param {Object} state - Состояние store
   * @returns {boolean} Состояние загрузки
   */
  isLoading: (state) => state.loading,

  /**
   * Получает информацию об ошибке
   * @param {Object} state - Состояние store
   * @returns {string|null} Сообщение об ошибке или null
   */
  getError: (state) => state.error,
}
